'use client';

import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import IconButton from '@mui/material/IconButton';

interface NotificationData {
  title?: string;
  body?: string;
  messageId?: string;
  type?: string;
  data?: {
    url?: string;
    click_action?: string;
  };
}

interface DisplayPushNotificationToastOptions {
  notification: NotificationData;
  onClose?: () => void;
  onClick?: (url: string) => void;
}

/**
 * Custom toast content component matching app design system
 */
const PushNotificationToastContent: React.FC<{
  title: string;
  body: string;
  onDismiss: () => void;
}> = ({ title, body, onDismiss }) => {
  const [fullDetailOpen, setFullDetailOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (!fullDetailOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFullDetailOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [fullDetailOpen]);
  const [hasMore, setHasMore] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const maxHeight = 84; // 4 lines * 1.5 line-height * 14px
  
  // Split by newlines for display
  const lines = body.split('\n');
  
  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling
    setIsExpanded(!isExpanded);
  };

  // Check if content exceeds max height
  useEffect(() => {
    const checkContentHeight = () => {
      if (contentRef.current) {
        // Temporarily remove maxHeight to measure full content
        const originalMaxHeight = contentRef.current.style.maxHeight;
        const originalOverflow = contentRef.current.style.overflow;
        contentRef.current.style.maxHeight = 'none';
        contentRef.current.style.overflow = 'visible';
        const fullHeight = contentRef.current.scrollHeight;
        contentRef.current.style.maxHeight = originalMaxHeight;
        contentRef.current.style.overflow = originalOverflow;
        
        // Check if content height exceeds max height OR if there are more than 4 newline-separated lines
        setHasMore(fullHeight > maxHeight || lines.length > 4);
      }
    };

    // Check immediately
    checkContentHeight();
    
    // Also check after a short delay to ensure content is fully rendered
    const timeoutId = setTimeout(checkContentHeight, 100);
    
    // Check on window resize
    window.addEventListener('resize', checkContentHeight);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkContentHeight);
    };
  }, [body, lines.length, maxHeight]);

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling
    onDismiss();
  };

  const handleViewItem = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFullDetailOpen(true);
  };

  const handleCloseFullDetail = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setFullDetailOpen(false);
  };

  /** Full-notification modal: position, size, drag (header), resize (SE corner) */
  const [modalPos, setModalPos] = useState({ left: 0, top: 0 });
  const [modalSize, setModalSize] = useState({ width: 520, height: 480 });
  const dragRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    startLeft: 0,
    startTop: 0,
  });
  const resizeRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    startW: 0,
    startH: 0,
  });
  const modalPosRef = useRef(modalPos);
  const modalSizeRef = useRef(modalSize);
  const modalBodyMeasureRef = useRef<HTMLDivElement>(null);
  modalPosRef.current = modalPos;
  modalSizeRef.current = modalSize;

  /** Initial open: width fixed, height fit content (capped by viewport) */
  useLayoutEffect(() => {
    if (!fullDetailOpen) return;
    const w = 520;
    const maxH = Math.round(window.innerHeight * 0.9);
    const measure = () => {
      const el = modalBodyMeasureRef.current;
      const headerFooterReserve = 140; // header + footer + borders (~)
      const bodyH = el ? el.scrollHeight : 120;
      const h = Math.min(maxH, Math.max(200, headerFooterReserve + bodyH));
      setModalSize({ width: w, height: h });
      setModalPos({
        left: Math.max(16, Math.round((window.innerWidth - w) / 2)),
        top: Math.max(16, Math.round((window.innerHeight - h) / 2)),
      });
    };
    requestAnimationFrame(() => requestAnimationFrame(measure));
  }, [fullDetailOpen, title, body]);

  useEffect(() => {
    if (!fullDetailOpen) return;
    const onMove = (e: MouseEvent) => {
      if (dragRef.current.active) {
        const dx = e.clientX - dragRef.current.startX;
        const dy = e.clientY - dragRef.current.startY;
        const { width, height } = modalSizeRef.current;
        const maxL = Math.max(0, window.innerWidth - width);
        const maxT = Math.max(0, window.innerHeight - height);
        setModalPos({
          left: Math.min(maxL, Math.max(0, dragRef.current.startLeft + dx)),
          top: Math.min(maxT, Math.max(0, dragRef.current.startTop + dy)),
        });
      }
      if (resizeRef.current.active) {
        const dw = e.clientX - resizeRef.current.startX;
        const dh = e.clientY - resizeRef.current.startY;
        const { left, top } = modalPosRef.current;
        setModalSize({
          width: Math.min(
            window.innerWidth - left - 8,
            Math.max(320, resizeRef.current.startW + dw)
          ),
          height: Math.min(
            window.innerHeight - top - 8,
            Math.max(200, resizeRef.current.startH + dh)
          ),
        });
      }
    };
    const onUp = () => {
      dragRef.current.active = false;
      resizeRef.current.active = false;
      document.body.style.cursor = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [fullDetailOpen]);

  const onHeaderMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    dragRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      startLeft: modalPos.left,
      startTop: modalPos.top,
    };
  };

  const onResizeMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    document.body.style.cursor = 'nwse-resize';
    resizeRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      startW: modalSizeRef.current.width,
      startH: modalSizeRef.current.height,
    };
  };

  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };

  // Capture phase: stop click from bubbling so container's closeOnClick / any parent link never runs.
  // Allow clicks on buttons (DISMISS, VIEW) to work.
  const handleContentClickCapture = (e: React.MouseEvent) => {
    const target = e.target as Node;
    if (target && typeof (target as HTMLElement).closest === 'function' && (target as HTMLElement).closest('button')) {
      return;
    }
    e.stopPropagation();
    e.preventDefault();
  };

  const fullDetailModal =
    fullDetailOpen && typeof document !== 'undefined'
      ? createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="push-notification-full-title"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 99999,
              backgroundColor: 'rgba(0, 0, 0, 0.45)',
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            <div
              role="presentation"
              onClick={() => handleCloseFullDetail()}
              style={{
                position: 'absolute',
                inset: 0,
              }}
              aria-hidden
            />
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'fixed',
                left: modalPos.left,
                top: modalPos.top,
                width: modalSize.width,
                height: modalSize.height,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#ffffff',
                borderRadius: '10px',
                boxShadow: '0 8px 32px rgba(21, 60, 127, 0.25)',
                border: '1px solid rgba(21, 60, 127, 0.15)',
                overflow: 'hidden',
                minWidth: 320,
                minHeight: 200,
                boxSizing: 'border-box',
              }}
            >
              {/* Draggable header — primary blue, white text */}
              <div
                onMouseDown={onHeaderMouseDown}
                style={{
                  flexShrink: 0,
                  padding: '14px 8px 14px 12px',
                  backgroundColor: '#153C7F',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'grab',
                  userSelect: 'none',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
                }}
              >
                <DragIndicatorIcon
                  sx={{
                    color: 'rgba(255, 255, 255, 0.85)',
                    fontSize: '22px',
                    flexShrink: 0,
                  }}
                  aria-hidden
                />
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    backgroundColor: 'rgba(255, 255, 255, 0.18)',
                    flexShrink: 0,
                  }}
                >
                  <NotificationsIcon sx={{ color: '#fff', fontSize: '24px' }} />
                </div>
                <h2
                  id="push-notification-full-title"
                  style={{
                    margin: 0,
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#fff',
                    lineHeight: 1.4,
                    wordWrap: 'break-word',
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {title}
                </h2>
                <IconButton
                  type="button"
                  onClick={(e) => handleCloseFullDetail(e)}
                  aria-label="Close notification"
                  size="small"
                  sx={{
                    color: '#fff',
                    flexShrink: 0,
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' },
                  }}
                >
                  <CloseIcon fontSize="medium" />
                </IconButton>
              </div>
              <div
                ref={modalBodyMeasureRef}
                style={{
                  padding: '16px 20px',
                  overflowY: 'auto',
                  flex: 1,
                  minHeight: 0,
                  fontSize: '16px',
                  fontWeight: 400,
                  color: '#4D4D4D',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                }}
              >
                {body || '—'}
              </div>
              <div
                style={{
                  flexShrink: 0,
                  padding: '16px 20px',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '8px',
                  borderTop: '1px solid rgba(21, 60, 127, 0.08)',
                  backgroundColor: '#fafafa',
                }}
              >
                <button
                  type="button"
                  onClick={(e) => handleCloseFullDetail(e)}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #F3F1FF',
                    background: '#F3F1FF',
                    color: '#767C88',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 400,
                    fontFamily: 'Poppins, sans-serif',
                    cursor: 'pointer',
                    textTransform: 'none',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#E8E5FF';
                    e.currentTarget.style.borderColor = '#E8E5FF';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#F3F1FF';
                    e.currentTarget.style.borderColor = '#F3F1FF';
                  }}
                >
                  CLOSE
                </button>
              </div>
              {/* SE resize handle — primary blue */}
              <div
                onMouseDown={onResizeMouseDown}
                role="presentation"
                title="Resize"
                style={{
                  position: 'absolute',
                  right: 0,
                  bottom: 0,
                  width: 22,
                  height: 22,
                  cursor: 'nwse-resize',
                  backgroundColor: '#153C7F',
                  clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
                  borderBottomRightRadius: '10px',
                  boxShadow: 'inset -2px -2px 0 rgba(255,255,255,0.2)',
                }}
                aria-hidden
              />
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      {fullDetailModal}
    <div
      role="presentation"
      onClick={handleContentClick}
      onClickCapture={handleContentClickCapture}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '4px 0',
        fontFamily: 'Poppins, sans-serif',
        minWidth: '300px',
        maxWidth: '400px',
      }}
    >
      {/* Notification Icon */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '40px',
          height: '40px',
          borderRadius: '10px',
          backgroundColor: '#F3F1FF',
          flexShrink: 0,
        }}
      >
        <NotificationsIcon
          sx={{
            color: '#153C7F',
            fontSize: '24px',
          }}
        />
      </div>

      {/* Content */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          minWidth: 0,
        }}
      >
        {/* Title */}
        <div
          style={{
            fontSize: '16px',
            fontWeight: 600,
            color: '#153C7F',
            lineHeight: '1.4',
            wordWrap: 'break-word',
          }}
        >
          {title}
        </div>

        {/* Body */}
        {body && (
          <div
            style={{
              fontSize: '14px',
              fontWeight: 400,
              color: '#4D4D4D',
              lineHeight: '1.5',
              wordWrap: 'break-word',
            }}
          >
            <div
              ref={contentRef}
              style={{
                overflow: 'hidden',
                maxHeight: isExpanded ? 'none' : `${maxHeight}px`,
                transition: 'max-height 0.3s ease-in-out',
              }}
            >
              {lines.map((line, index) => (
                <React.Fragment key={index}>
                  {line}
                  {index < lines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </div>
            {hasMore && (
              <button
                onClick={handleToggleExpand}
                style={{
                  marginTop: '4px',
                  padding: 0,
                  border: 'none',
                  background: 'none',
                  color: '#153C7F',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'Poppins, sans-serif',
                  textDecoration: 'underline',
                }}
              >
                {isExpanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div
          style={{
            display: 'flex',
            gap: '8px',
            marginTop: '8px',
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={handleDismiss}
            style={{
              padding: '6px 12px',
              border: '1px solid #767C88',
              background: '#F3F1FF',
              color: '#767C88',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 400,
              fontFamily: 'Poppins, sans-serif',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              textTransform: 'none',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#E8E5FF';
              e.currentTarget.style.borderColor = '#E8E5FF';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#F3F1FF';
              e.currentTarget.style.borderColor = '#767C88';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            DISMISS
          </button>
          <button
            onClick={handleViewItem}
            style={{
              padding: '6px 12px',
              border: 'none',
              background: '#63A821',
              color: '#fff',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 600,
              fontFamily: 'Poppins, sans-serif',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              textTransform: 'none',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#4e8c1a';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#63A821';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            VIEW
          </button>
        </div>
      </div>
    </div>
    </>
  );
};

/**
 * Displays a toast notification for push notifications
 * Handles deduplication, tracking, and UI display
 */
export async function displayPushNotificationToast({
  notification,
  onClose,
  onClick,
}: DisplayPushNotificationToastOptions): Promise<void> {
  const title = notification?.title || 'New Notification';
  const body = notification?.body || '';
  const messageId = notification?.messageId || 'unknown';
  const notificationUrl = notification?.data?.url || notification?.data?.click_action || '/';

  console.log('[PushNotificationToast] Notification details:', {
    title,
    body,
    messageId,
    notification: notification,
  });

  // Track displayed toasts to prevent duplicates - ATOMIC CHECK AND MARK
  if (typeof window !== 'undefined') {
    if (!(window as any).__displayedNotificationIds) {
      (window as any).__displayedNotificationIds = new Set<string>();
    }

    // Check if a toast with this messageId already exists in the DOM
    const toastId = `push-${messageId}`;
    const existingToast = document.querySelector(`[id*="${toastId}"]`) ||
      document.querySelector(`[data-toast-id="${toastId}"]`);

    // Check if this notification was already displayed (atomic check)
    const alreadyDisplayed = (window as any).__displayedNotificationIds.has(messageId) || !!existingToast;

    if (alreadyDisplayed) {
      console.log('[PushNotificationToast] Skipping toast - notification already displayed:', messageId, {
        inSet: (window as any).__displayedNotificationIds.has(messageId),
        inDOM: !!existingToast,
      });
      return;
    }

    // Mark this notification as displayed IMMEDIATELY (before any async operations)
    (window as any).__displayedNotificationIds.add(messageId);
    console.log('[PushNotificationToast] Marked notification as displayed:', messageId);

    // Clean up old IDs after 1 hour to prevent memory leaks
    setTimeout(() => {
      if ((window as any).__displayedNotificationIds) {
        (window as any).__displayedNotificationIds.delete(messageId);
      }
    }, 60 * 60 * 1000); // 1 hour
  }

  // Track push notification toasts BEFORE any API calls to prevent dismissal
  if (typeof window !== 'undefined') {
    if (!(window as any).__pushNotificationToastIds) {
      (window as any).__pushNotificationToastIds = new Set<string>();
    }
    // Track that a push notification toast will be active (use messageId as identifier)
    (window as any).__pushNotificationToastIds.add(messageId);
    console.log('[PushNotificationToast] Tracked push notification toast (before display):', messageId);
  }

  // Use requestAnimationFrame + setTimeout to ensure ToastContainer is ready
  requestAnimationFrame(() => {
    setTimeout(() => {
      try {
        // Check if ToastContainer exists in DOM
        const toastContainer = document.querySelector('.Toastify__toast-container');
        console.log('[PushNotificationToast] ToastContainer check:', {
          exists: !!toastContainer,
          toastContainerStyles: toastContainer ? window.getComputedStyle(toastContainer as Element) : null,
        });

        // Check if toast is available
        if (typeof toast === 'undefined' || !toast.info) {
          console.error('[PushNotificationToast] ❌ Toast is not available. Make sure ToastContainer is rendered in the app.');
          console.error('[PushNotificationToast] Toast check:', {
            toastDefined: typeof toast !== 'undefined',
            toastInfoDefined: typeof toast !== 'undefined' && typeof toast.info !== 'undefined',
            windowToast: (window as any).toast,
          });
          // Clean up tracking if toast is not available
          if (typeof window !== 'undefined' && (window as any).__pushNotificationToastIds) {
            (window as any).__pushNotificationToastIds.delete(messageId);
          }
          return;
        }

        console.log('[PushNotificationToast] Attempting to display toast for notification...', {
          title,
          body,
          messageId,
          notificationType: notification.type || 'background',
          hasToast: typeof toast !== 'undefined',
          hasToastInfo: typeof toast.info !== 'undefined',
          trackingActive: (window as any).__pushNotificationToastIds?.has(messageId),
          toastContainerExists: !!document.querySelector('.Toastify__toast-container'),
        });

        // Double-check before displaying - toast might have been created by another handler
        const checkToastId = `push-${messageId}`;
        const existingToastCheck = document.querySelector(`[id*="${checkToastId}"]`) ||
          document.querySelector(`[data-toast-id="${checkToastId}"]`);

        if (existingToastCheck) {
          console.log('[PushNotificationToast] ⚠️ Toast already exists in DOM, skipping display:', messageId);
          // Clean up tracking since toast already exists
          if (typeof window !== 'undefined' && (window as any).__pushNotificationToastIds) {
            (window as any).__pushNotificationToastIds.delete(messageId);
          }
          return;
        }

        // Verify it's still in the displayed set (race condition protection)
        if (!(window as any).__displayedNotificationIds?.has(messageId)) {
          console.log('[PushNotificationToast] ⚠️ Notification no longer in displayed set, skipping:', messageId);
          if (typeof window !== 'undefined' && (window as any).__pushNotificationToastIds) {
            (window as any).__pushNotificationToastIds.delete(messageId);
          }
          return;
        }

        // Check if toast with this ID already exists - if so, dismiss it first to override
        const existingToastElement = document.querySelector(`[id*="${checkToastId}"]`) ||
          document.querySelector(`[data-toast-id="${checkToastId}"]`);
        if (existingToastElement) {
          console.log('[PushNotificationToast] Toast already exists, dismissing to override:', checkToastId);
          try {
            toast.dismiss(checkToastId);
          } catch (error) {
            console.warn('[PushNotificationToast] Error dismissing existing toast:', error);
          }
        }

        // ============================================
        // TOAST UI CONFIGURATION - CUSTOM DESIGN
        // ============================================
        // Handler for dismiss button
        const handleDismiss = () => {
          console.log('[PushNotificationToast] DISMISS button clicked:', messageId);
          // Dismiss using the configured toast ID
          try {
            toast.dismiss(checkToastId);
          } catch (error) {
            console.warn('[PushNotificationToast] Error dismissing toast:', error);
          }
          // Fallback: try to remove the toast element directly if dismiss doesn't work
          setTimeout(() => {
            const toastElement = document.querySelector(`[id*="${checkToastId}"]`) ||
              document.querySelector(`[data-toast-id="${checkToastId}"]`);
            if (toastElement) {
              const toastParent = toastElement.closest('.Toastify__toast');
              if (toastParent) {
                (toastParent as HTMLElement).style.transition = 'opacity 0.3s';
                (toastParent as HTMLElement).style.opacity = '0';
                setTimeout(() => {
                  toastParent.remove();
                }, 300);
              }
            }
          }, 100);
          // Clean up tracking when toast is dismissed
          if (typeof window !== 'undefined' && (window as any).__pushNotificationToastIds) {
            (window as any).__pushNotificationToastIds.delete(messageId);
          }
          onClose?.();
        };

        // VIEW opens full-notification modal inside toast content (no redirect)

        // Create custom toast content component
        const toastContent = React.createElement(PushNotificationToastContent, {
          title,
          body,
          onDismiss: handleDismiss,
        });

        const toastId = toast.info(toastContent, {
          position: 'top-right' as const,
          autoClose: false,
          hideProgressBar: true,
          closeOnClick: false, // Disabled since we have explicit buttons
          pauseOnHover: true,
          draggable: true,
          toastId: checkToastId,
          className: 'push-notification-toast',
          onClick: (e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
          },
          style: {
            borderRadius: '10px',
            boxShadow: '0 2px 8px rgba(21, 60, 127, 0.15)',
            border: '1px solid rgba(21, 60, 127, 0.1)',
            backgroundColor: '#ffffff',
            padding: '16px',
            fontFamily: 'Poppins, sans-serif',
          },
          onClose: () => {
            console.log('[PushNotificationToast] Toast closed callback triggered:', messageId);
            // Clean up tracking when toast is closed
            // BUT: Keep it in __displayedNotificationIds to prevent re-display (it will auto-remove after 1 hour)
            if (typeof window !== 'undefined' && (window as any).__pushNotificationToastIds) {
              (window as any).__pushNotificationToastIds.delete(messageId);
            }
            // Don't remove from __displayedNotificationIds here - let the timeout handle it
            // This prevents the same notification from being displayed again if another handler fires
            onClose?.();
          },
        });
        // ============================================

        console.log('[PushNotificationToast] ✅ toast.info returned:', {
          messageId,
          notificationUrl,
          notificationType: notification.type || 'background',
          toastId,
          toastIdType: typeof toastId,
        });

        // Verify toast is in DOM immediately and after delays
        setTimeout(() => {
          const toastElement = document.querySelector(`[id*="${toastId}"]`) ||
            document.querySelector(`[data-toast-id*="${toastId}"]`) ||
            document.querySelector(`[id="${toastId}"]`) ||
            document.querySelector('.Toastify__toast');
          const allToasts = document.querySelectorAll('.Toastify__toast');
          console.log('[PushNotificationToast] Toast DOM check (50ms):', {
            toastId,
            foundById: !!toastElement,
            totalToasts: allToasts.length,
            allToastIds: Array.from(allToasts).map((t: any) => t.id || t.getAttribute('data-toast-id') || 'no-id'),
          });
        }, 50);

        setTimeout(() => {
          const toastElement = document.querySelector(`[id*="${toastId}"]`) ||
            document.querySelector(`[data-toast-id*="${toastId}"]`) ||
            document.querySelector(`[id="${toastId}"]`) ||
            document.querySelector('.Toastify__toast');
          const allToasts = document.querySelectorAll('.Toastify__toast');
          if (!toastElement && allToasts.length === 0) {
            console.error('[PushNotificationToast] ❌ Toast was not created! Attempting fallback...');
            // Try fallback - use custom component with minimal options
            try {
              const fallbackContent = React.createElement(PushNotificationToastContent, {
                title,
                body,
                onDismiss: handleDismiss,
              });
              const fallbackToastId = toast.info(fallbackContent, {
                toastId: checkToastId,
                autoClose: false,
                closeOnClick: false,
              });
              console.log('[PushNotificationToast] Fallback toast displayed:', fallbackToastId);
            } catch (fallbackError) {
              console.error('[PushNotificationToast] Fallback toast also failed:', fallbackError);
            }
          } else {
            console.log('[PushNotificationToast] ✅ Toast verified in DOM (200ms):', {
              toastId,
              found: !!toastElement,
              totalToasts: allToasts.length,
            });
          }

          // Apply custom styles to the toast element
          if (toastElement) {
            const toastEl = toastElement as HTMLElement;
            // Ensure custom styles are applied
            toastEl.style.borderRadius = '10px';
            toastEl.style.boxShadow = '0 2px 8px rgba(21, 60, 127, 0.15)';
            toastEl.style.border = '1px solid rgba(21, 60, 127, 0.1)';
            toastEl.style.backgroundColor = '#ffffff';
            toastEl.style.fontFamily = 'Poppins, sans-serif';
            
            // Add hover effect
            toastEl.style.transition = 'all 0.2s ease-in-out';
            toastEl.addEventListener('mouseenter', () => {
              toastEl.style.boxShadow = '0 4px 12px rgba(21, 60, 127, 0.2)';
              toastEl.style.transform = 'translateY(-2px)';
            });
            toastEl.addEventListener('mouseleave', () => {
              toastEl.style.boxShadow = '0 2px 8px rgba(21, 60, 127, 0.15)';
              toastEl.style.transform = 'translateY(0)';
            });

            // Fix close button - ensure it properly dismisses the toast
            const closeButton = toastEl.querySelector('.Toastify__close-button') as HTMLElement;
            if (closeButton) {
              // Store values for the close button handler
              const toastIdToDismiss = checkToastId;
              const returnedToastId = toastId;
              const messageIdToClean = messageId;
              
              // Remove existing click listeners by cloning
              const newCloseButton = closeButton.cloneNode(true) as HTMLElement;
              closeButton.parentNode?.replaceChild(newCloseButton, closeButton);
              
              // Add our click handler
              newCloseButton.addEventListener('click', (e) => {
                e.stopPropagation();
                e.preventDefault();
                console.log('[PushNotificationToast] Close button (X) clicked:', messageIdToClean);
                // Dismiss using the configured toast ID
                try {
                  toast.dismiss(toastIdToDismiss);
                } catch (error) {
                  console.warn('[PushNotificationToast] Error dismissing toast by ID:', error);
                }
                // Also try dismissing by the returned toastId if different
                if (returnedToastId && returnedToastId !== toastIdToDismiss) {
                  try {
                    toast.dismiss(returnedToastId);
                  } catch (error) {
                    console.warn('[PushNotificationToast] Error dismissing toast by returned ID:', error);
                  }
                }
                // As a fallback, try to find and remove the toast element directly
                const toastToRemove = document.querySelector(`[id*="${toastIdToDismiss}"]`) ||
                  document.querySelector(`[data-toast-id="${toastIdToDismiss}"]`);
                if (toastToRemove) {
                  const toastParent = toastToRemove.closest('.Toastify__toast');
                  if (toastParent) {
                    (toastParent as HTMLElement).style.opacity = '0';
                    setTimeout(() => {
                      toastParent.remove();
                    }, 300);
                  }
                }
                // Clean up tracking
                if (typeof window !== 'undefined' && (window as any).__pushNotificationToastIds) {
                  (window as any).__pushNotificationToastIds.delete(messageIdToClean);
                }
                onClose?.();
              }, { once: false });
            }
          }
        }, 200);
      } catch (toastError) {
        console.error('[PushNotificationToast] ❌ Failed to display toast notification:', toastError);
        console.error('[PushNotificationToast] Toast error details:', {
          error: toastError,
          errorType: typeof toastError,
          errorMessage: toastError instanceof Error ? toastError.message : String(toastError),
          stack: toastError instanceof Error ? toastError.stack : undefined,
        });
        // Clean up tracking on error
        if (typeof window !== 'undefined' && (window as any).__pushNotificationToastIds) {
          (window as any).__pushNotificationToastIds.delete(messageId);
        }
      }
    }, 150); // Small delay to ensure ToastContainer is ready
  });
}

